import { useState } from 'react'
import type { EAQCFinding, EAQCFindingListItem, EAQCSeverity, EAQCFindingStatus } from '../../types/ea'
import EAVendorBadge from './EAVendorBadge'

interface EAFindingCardProps {
  finding: EAQCFindingListItem | EAQCFinding
  onAcceptRisk?: () => void
  onResolve?: () => void
}

const SEVERITY_COLORS: Record<EAQCSeverity, string> = {
  critical: '#ef4444',
  high: '#f97316',
  med: '#f59e0b',
  low: '#10b981',
}

const SEVERITY_LABELS: Record<EAQCSeverity, string> = {
  critical: 'Critical',
  high: 'High',
  med: 'Medium',
  low: 'Low',
}

const STATUS_COLORS: Record<EAQCFindingStatus, string> = {
  open: '#ef4444',
  remediation_in_progress: '#f59e0b',
  resolved: '#10b981',
  accepted_risk: '#6b7280',
  escalated: '#dc2626',
}

const STATUS_LABELS: Record<EAQCFindingStatus, string> = {
  open: 'Open',
  remediation_in_progress: 'Remediation In Progress',
  resolved: 'Resolved',
  accepted_risk: 'Accepted Risk',
  escalated: 'Escalated',
}

function isFullFinding(finding: EAQCFindingListItem | EAQCFinding): finding is EAQCFinding {
  return 'description' in finding && 'recommendation' in finding
}

export default function EAFindingCard({ finding, onAcceptRisk, onResolve }: EAFindingCardProps) {
  const [expanded, setExpanded] = useState(false)
  const severityColor = SEVERITY_COLORS[finding.severity]
  const statusColor = STATUS_COLORS[finding.status]
  const severityLabel = SEVERITY_LABELS[finding.severity]
  const statusLabel = STATUS_LABELS[finding.status]
  const hasFull = isFullFinding(finding)
  const canAct = finding.status === 'open' || finding.status === 'remediation_in_progress'

  return (
    <div
      className="rounded-xl p-5 transition-all duration-200"
      style={{
        background: 'rgba(30, 41, 59, 0.5)',
        border: `1px solid ${finding.status === 'open' && finding.severity === 'critical' ? 'rgba(239, 68, 68, 0.3)' : 'rgba(100, 180, 255, 0.1)'}`,
      }}
    >
      {/* Header: finding code + title + badges */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center space-x-2 mb-1">
            <span
              className="text-xs font-mono px-1.5 py-0.5 rounded"
              style={{
                background: 'rgba(100, 116, 139, 0.2)',
                color: 'rgba(148, 163, 184, 0.8)',
              }}
            >
              {finding.finding_code}
            </span>
            {finding.recurrence_count > 1 && (
              <span
                className="text-xs px-1.5 py-0.5 rounded-full flex items-center space-x-1"
                style={{
                  background: 'rgba(245, 158, 11, 0.15)',
                  color: '#f59e0b',
                  border: '1px solid rgba(245, 158, 11, 0.3)',
                }}
              >
                <i className="fa-solid fa-repeat text-[10px]"></i>
                <span>{finding.recurrence_count}x</span>
              </span>
            )}
          </div>
          <h4 className="text-sm font-semibold leading-tight" style={{ color: '#e2e8f0' }}>
            {finding.title}
          </h4>
        </div>
        <div className="flex flex-col items-end space-y-1.5 ml-3 flex-shrink-0">
          {/* Severity badge */}
          <span
            className="px-2.5 py-1 text-xs font-medium rounded-full"
            style={{
              background: `${severityColor}22`,
              color: severityColor,
              border: `1px solid ${severityColor}44`,
            }}
          >
            {severityLabel}
          </span>
          {/* Status badge */}
          <span
            className="px-2.5 py-1 text-xs font-medium rounded-full"
            style={{
              background: `${statusColor}22`,
              color: statusColor,
              border: `1px solid ${statusColor}44`,
            }}
          >
            {statusLabel}
          </span>
        </div>
      </div>

      {/* Metadata row: vendor, category, responsible agent */}
      <div className="flex flex-wrap items-center gap-2 mb-3">
        {finding.vendor && <EAVendorBadge vendor={finding.vendor} size="sm" />}
        <span
          className="px-2 py-0.5 text-xs font-medium rounded-md"
          style={{
            background: 'rgba(139, 92, 246, 0.15)',
            color: '#a78bfa',
            border: '1px solid rgba(139, 92, 246, 0.3)',
          }}
        >
          {finding.category}
        </span>
        <span
          className="px-2 py-0.5 text-xs font-medium rounded-md"
          style={{
            background: 'rgba(6, 182, 212, 0.15)',
            color: '#67e8f9',
            border: '1px solid rgba(6, 182, 212, 0.3)',
          }}
        >
          {finding.responsible_agent}
        </span>
      </div>

      {/* Expandable description + recommendation */}
      {hasFull && (
        <>
          <button
            onClick={() => setExpanded(!expanded)}
            className="flex items-center space-x-1.5 text-xs font-medium mb-2 transition-colors duration-200"
            style={{ color: 'rgba(148, 163, 184, 0.7)' }}
            aria-expanded={expanded}
            aria-label={expanded ? 'Collapse details' : 'Expand details'}
          >
            <i className={`fa-solid fa-chevron-${expanded ? 'down' : 'right'} text-[10px]`}></i>
            <span>{expanded ? 'Hide Details' : 'Show Details'}</span>
          </button>
          {expanded && (
            <div className="space-y-3 mb-3">
              <div
                className="rounded-lg p-3 text-sm"
                style={{
                  background: 'rgba(15, 23, 42, 0.4)',
                  border: '1px solid rgba(100, 180, 255, 0.08)',
                }}
              >
                <p className="text-xs font-semibold uppercase tracking-wider mb-1.5" style={{ color: 'rgba(100, 116, 139, 0.6)' }}>
                  Description
                </p>
                <p style={{ color: 'rgba(226, 232, 240, 0.9)' }}>{finding.description}</p>
              </div>
              {finding.expected && (
                <div
                  className="rounded-lg p-3 text-sm"
                  style={{
                    background: 'rgba(16, 185, 129, 0.05)',
                    border: '1px solid rgba(16, 185, 129, 0.15)',
                  }}
                >
                  <p className="text-xs font-semibold uppercase tracking-wider mb-1.5" style={{ color: '#10b981' }}>
                    Expected
                  </p>
                  <p style={{ color: 'rgba(226, 232, 240, 0.9)' }}>{finding.expected}</p>
                </div>
              )}
              {finding.actual && (
                <div
                  className="rounded-lg p-3 text-sm"
                  style={{
                    background: 'rgba(239, 68, 68, 0.05)',
                    border: '1px solid rgba(239, 68, 68, 0.15)',
                  }}
                >
                  <p className="text-xs font-semibold uppercase tracking-wider mb-1.5" style={{ color: '#ef4444' }}>
                    Actual
                  </p>
                  <p style={{ color: 'rgba(226, 232, 240, 0.9)' }}>{finding.actual}</p>
                </div>
              )}
              <div
                className="rounded-lg p-3 text-sm"
                style={{
                  background: 'rgba(59, 130, 246, 0.05)',
                  border: '1px solid rgba(59, 130, 246, 0.15)',
                }}
              >
                <p className="text-xs font-semibold uppercase tracking-wider mb-1.5" style={{ color: '#3b82f6' }}>
                  Recommendation
                </p>
                <p style={{ color: 'rgba(226, 232, 240, 0.9)' }}>{finding.recommendation}</p>
              </div>
              {finding.resolution_notes && (
                <div
                  className="rounded-lg p-3 text-sm"
                  style={{
                    background: 'rgba(100, 116, 139, 0.05)',
                    border: '1px solid rgba(100, 116, 139, 0.15)',
                  }}
                >
                  <p className="text-xs font-semibold uppercase tracking-wider mb-1.5" style={{ color: '#94a3b8' }}>
                    Resolution Notes
                  </p>
                  <p style={{ color: 'rgba(226, 232, 240, 0.9)' }}>{finding.resolution_notes}</p>
                </div>
              )}
            </div>
          )}
        </>
      )}

      {/* Action buttons */}
      {canAct && (onAcceptRisk || onResolve) && (
        <div className="flex items-center justify-end space-x-2 pt-2" style={{ borderTop: '1px solid rgba(100, 180, 255, 0.08)' }}>
          {onAcceptRisk && (
            <button
              onClick={onAcceptRisk}
              className="px-3 py-1.5 text-xs font-medium rounded-lg transition-all duration-200 hover:scale-105"
              style={{
                background: 'rgba(100, 116, 139, 0.1)',
                border: '1px solid rgba(100, 116, 139, 0.2)',
                color: '#94a3b8',
              }}
            >
              <i className="fa-solid fa-shield-halved mr-1.5"></i>
              Accept Risk
            </button>
          )}
          {onResolve && (
            <button
              onClick={onResolve}
              className="px-3 py-1.5 text-xs font-medium rounded-lg transition-all duration-200 hover:scale-105"
              style={{
                background: 'rgba(16, 185, 129, 0.1)',
                border: '1px solid rgba(16, 185, 129, 0.2)',
                color: '#34d399',
              }}
            >
              <i className="fa-solid fa-check-circle mr-1.5"></i>
              Mark Resolved
            </button>
          )}
        </div>
      )}
    </div>
  )
}
